import {Injectable} from '@angular/core';
import {BackendHttpService} from '../../core/backend-http.service';
import {apiEndpoints, listOrderedPlatforms} from '../../app.constants';
import {Observable} from 'rxjs/Observable';
import {KpiReport, Report, ReportCreation, SegmentIndustry, SegmentInfluencer} from '../../../models/measure/report';
import {MeasureSearchCriteria} from '../../../models/measure/measure-search-criteria';
import {TranslateService} from '@ngx-translate/core';
import {CommonService} from '../common/common.service';
import {Router} from '@angular/router';
import {RoiReport, RoiReportData, RoiReportKpi, RoiSetting} from '../../../models/measure/roi-report';
import {TopicSegmentReport} from '../../../models/measure/topic-segment-report';
import {DatePipe} from '@angular/common';
import {DateRange} from '../../../models/date-range';
import {Kpi, KpiItem} from '../../../models/common';
import {GaugeCircleChartData, TopExposure} from '../../../models/influencer';
import {OnnaReportResponse, OonaPost} from '../../../models/oona-post';
import {UserService} from '../user.service';
import {ReferenceDataService} from '../reference-data/reference-data.service';

@Injectable()
export class MeasureService extends BackendHttpService<any> {

  private listPostOfROI: any[];

  constructor(protected router: Router,
              protected translate: TranslateService,
              private commonService: CommonService,
              private userService: UserService,
              private referenceService: ReferenceDataService,
              private datePipe: DatePipe) {
    super(router, translate);
  }

  /**
   * API to load list measure report of a company
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getListMeasureReport(companyId): Observable<Report[]> {
    return this.get(apiEndpoints.getMeasureReportList, {selected_company_id: companyId}).map(res =>
      res && res.data ? res.data.map((report) => Object.assign({}, report, {
        id: report.report_id,
        name: report.report_name
      })) : []
    );
  }

  /**
   * API to create or update a measure report
   * @param companyId
   * @returns {Observable<Report>}
   */
  public createOrUpdateReport(report: ReportCreation) {
    // If and ID is assigned, the report exists and we update it
    if (report.id > 0) {
      const updateReportUrl = apiEndpoints.report + '/' + report.id;
      return this.put(updateReportUrl, report);
    } else {
      // If report ID is 0 it's a new report and we create it
      return this.post(apiEndpoints.report, report);
    }
  }

  /**
   * API to get ROI report data
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getRoiReportData(measureCriteria: MeasureSearchCriteria) {
    const measureData = this.buildMeasureRequestData(measureCriteria);
    const data = Object.assign({}, measureData, {
      companyId: this.userService.getCompanyId()
    });
    return Observable.forkJoin(
      this.getInfluencerCount(measureData),
      this.getTotalROI(data),
      this.getGlobalKpi(measureData),
      this.getListPostOfROI(data)
    ).map((res) => {
      const roiData = new RoiReport();
      const influencerData = new RoiReportKpi();
      if (res[0]) {
        influencerData.name = res[0].name;
        influencerData.value = res[0].value;
      }
      let kpiData = [];
      if (res[2]) {
        kpiData = res[2].map(k => ({
          name: k.name,
          value: k.value
        }));
      }
      const totalRoiData = new RoiReportKpi();
      const currencyData = new RoiReportKpi();
      if (res[1]) {
        totalRoiData.value = res[1].totalROI;
        totalRoiData.name = 'Roi';
        currencyData.name = 'Currency';
        currencyData.value = res[1].currency ? res[1].currency : 0;
      }

      roiData.kpis = [influencerData, ...kpiData, totalRoiData, currencyData];
      roiData.datas = res[3] ? res[3] : [];
      roiData.totalRow = roiData.datas.length;
      return roiData;
    });
  }

  private getTotalROI(data): Observable<any> {
    return this.get(apiEndpoints.measureGetTotalReturnOnInvestments, data);
  }

  private getListPostOfROI(data): Observable<RoiReportData[]> {
    return this.get(apiEndpoints.measureGetTopReturnOnInvestments, data).map(res => res.data);
  }

  /**
   * API to get Performance Local Area report data
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getPerformanceLocalAreas(measureCriteria: MeasureSearchCriteria) {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetCountryReach, data).map(res =>
      res.localAreas.map(country =>
        Object.assign({}, country, {
            description: this.translate.instant('INFLUENCER.insight.platform_reach.impact',
              {impact: Number(country.contentPieChart.impact.value).toFixed(1)})
          }
        )
      )
    );
  }

  /**
   * API to get Performance Timeline for measure
   * @param params
   */
  public getPerformanceTimeline(params: MeasureSearchCriteria) {
    const data = this.buildMeasureRequestData(params);
    return this.get(apiEndpoints.measurePerformanceTimeline, data).map(res => {
      return this.commonService.normalizeDataForActivityLineChart(res.data.data);
    });
  }

  /**
   * this method use to load Performance KPI data of ES
   * @param {number} influencerId
   * @param {number} period
   * @returns {any}
   */
  public getPerformanceGlobalKPIsFromEs(params): Observable<Kpi> {
    const data = this.buildMeasureRequestData(params);

    return Observable.forkJoin(
      this.getMentionCount(data),
      this.getGlobalKpi(data)
    ).map((res) => {
      const kpiData = new Kpi();
      kpiData.mention = res[0];
      kpiData.impressions = res[1].find(kpi => kpi.name === 'impressions');
      kpiData.reach = res[1].find(kpi => kpi.name === 'reach');
      kpiData.engagement = res[1].find(kpi => kpi.name === 'engagement');
      kpiData.impact = res[1].find(kpi => kpi.name === 'impact');
      return kpiData;
    });
  }

  private getMentionCount(currentPeriod): Observable<KpiItem> {
    return this.get(apiEndpoints.measureGetMentionCount, currentPeriod).map(res => res.data);
  }

  private getGlobalKpi(currentPeriod): Observable<KpiItem[]> {
    return this.get(apiEndpoints.measureGetGlobalKpis, currentPeriod).map(res => res.data.KPIs);
  }

  private formatPercentageData(attributeName, lastValue, currentValue) {
    let percent;
    if (lastValue === 0) {
      percent = 0;
    }
    percent = (Math.abs(currentValue - lastValue) / lastValue) * 100;
    return {
      name: attributeName,
      value: currentValue,
      status: currentValue > lastValue ? 'asc' : 'desc',
      percent: percent
    };
  }

  /**
   * API to get Performance Mention TimeLine report data
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getPerformanceMentionTimeLineChart(measureCriteria: MeasureSearchCriteria) {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetMentionTimeline, data);
  }

  /**
   * API to get Exposure Global KPI report data
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getExposureGlobalKPIs(measureCriteria: MeasureSearchCriteria): Observable<KpiReport[]> {
    const currentData = this.buildMeasureRequestData(measureCriteria);

    return Observable.forkJoin(
      this.getMentionCount(currentData),
      this.getPostCount(currentData),
      this.getInfluencerCount(currentData),
      this.getGlobalKpi(currentData)
    ).map((res) => {
      const engagmentKpi = res[3].find(kpi => kpi.name === 'engagement');
      const engagements = new KpiReport(engagmentKpi);
      const mentions = new KpiReport(res[0]);
      const posts = new KpiReport(res[1]);
      const influencers = new KpiReport(res[2]);
      return [
        engagements,
        mentions,
        posts,
        influencers
      ];
    });
  }

  /**
   * get post count KPI from ES
   * @param previousPeriod
   * @param currentPeriod
   * @returns {Observable<KpiItem>}
   */
  private getPostCount(data): Observable<KpiItem> {
    return this.get(apiEndpoints.measureGetPostCount, data).map(res => res.data);
  }

  /**
   * get influencer count KPI from ES
   * @param previousPeriod
   * @param currentPeriod
   * @returns {Observable<KpiItem>}
   */
  private getInfluencerCount(data): Observable<KpiItem> {
    return this.get(apiEndpoints.measureGetInfluencerCount, data).map(res => res.data);
  }

  /**
   * load Top Exposure and Best Performing Exposure report data for measure
   * @param params
   * @returns {Observable<any>}
   */
  public getExposuresReport(measureCriteria: MeasureSearchCriteria) {
    const currentData = this.buildMeasureRequestData(measureCriteria);
    return Observable.forkJoin(
      this.get(apiEndpoints.measureGetTopExposurePosts, currentData),
      this.get(apiEndpoints.measureGetBestPerformingPosts, currentData),
    ).map((res) => ({
      exposures: {
        engagement: this.normalizeExposureData(res[0].exposures.engagement),
        impact: this.normalizeExposureData(res[0].exposures.impact),
        reach: this.normalizeExposureData(res[0].exposures.reach),
      },
      bestPreformingExposures: {
        contents: res[1].bestPreformingExposures.contents.map(expo => Object.assign({}, expo, {
          urlHash: expo.url_hash
        }))
      }
    }));
  }

  private normalizeExposureData(exposureData): TopExposure {
    return Object.assign({}, exposureData, {
      urlHash: exposureData.url_hash
    });
  }

  /**
   * load PlatformReach report data from ES
   * @param {MeasureSearchCriteria} measure
   * @returns {Observable<GaugeCircleChartData[]>}
   */
  public getPlatformReachReportData(measureCriteria: MeasureSearchCriteria): Observable<GaugeCircleChartData[]> {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetPlatformReach, data).map((res: any) =>
      res.platforms.map((platform) => Object.assign({}, platform, {
          description: this.translate.instant('INFLUENCER.insight.platform_reach.impact', {
            impact: Number(platform.contentPieChart.impact.value).toFixed(1)
          })
        })
      )
    );
  }

  /**
   * API to get Insight Benchmark Performance report data
   * @param measureCriteria
   * @returns {Observable<Report>}
   */
  public getInsightBenchmarkPerformance(measureCriteria: MeasureSearchCriteria) {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetBenchmarkPerformance, data);
  }

  /**
   * API to get Influencer Overview report data
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getSegmentInfluencer(measureCriteria: MeasureSearchCriteria): Observable<SegmentInfluencer> {
    const data = this.buildMeasureRequestData(measureCriteria);
    return Observable.forkJoin(
      this.get(apiEndpoints.measureGetInfluencerCount, data),
      this.get(apiEndpoints.measureGetInfluencerOverview, data),
    ).map(res => ({
        influencers: res[0].data,
        nationalities: res[1].nationalities,
        platforms: res[1].platforms.chartData.filter(ch => ch.value > 0).map(c => ({
          name: c.name,
          value: c.value
        }))
      })
    );
  }

  /**
   * API to get Interaction report data from ES
   * @param measureCriteria
   * @returns {Observable<Report>}
   */
  public getInfluencerPostInteractionsFromES(measureCriteria: MeasureSearchCriteria, limit: number) {
    const data = this.buildMeasureRequestData(measureCriteria, true);
    const params = Object.assign({}, data, {
      pageSize: limit
    });
    return Observable.forkJoin(
      this.get(apiEndpoints.measureGetIntereactionPosts, params),
      this.get(apiEndpoints.measureGetIntereactionSummary, params),
    ).map(res => ({
        posts: res[0].data.posts,
        totalRows: res[0].data.totalRows,
        kpi: res[1].data.kpi,
        pageNo: measureCriteria.page_no
      })
    );
  }

  /**
   * API to get Insight Channel Opportunity report data
   * @param measureCriteria
   * @returns {Observable<Report>}
   */
  public getInsightChannelOpportunity(measureCriteria: MeasureSearchCriteria) {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetInsightChannelOpportunity, data);
  }

  /**
   * load all supported modules
   * @returns {Observable<any>}
   */
  public loadListModules(): Observable<any[]> {
    return this.get(apiEndpoints.getSections).map((res: any) =>
      // transform the server-side data to client-side format data
      res && res.data ? res.data.map(module => ({
        id: module.id,
        active_icon_link: 'assets' + module.section_active_icon_link,
        description: module.section_description,
        height: module.section_height,
        inactive_icon_link: module.section_inactive_icon_link,
        preview_image: module.section_preview_image,
        show_up: module.section_show_up,
        template: module.section_template,
        name: module.section_title,
      })) : []
    );
  }

  /**
   * API to get Exposure Global KPI report data
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getSegmentTopics(measureCriteria: MeasureSearchCriteria): Observable<TopicSegmentReport> {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetSegmentTopics, data).map(
      res => this.transformToGaugeChartData(res.data)
    );
  }

  private transformToGaugeChartData(rawData): TopicSegmentReport {
    // prepare segment list data
    const allSegments = rawData.segments;
    const removedSegments = rawData.segmentRemoveList;

    // transform data before returing to component
    const dataCharts = allSegments.map(data => ({
        contentPieChart: {
          reach: {
            value: data.contentPieChart.value,
            status: data.contentPieChart.status || 'asc',
            percent: data.contentPieChart.percent || 0
          }
        },
        name: data.name,
        progressBar: {
          name: data.name,
          value: data.valuePieChart * 100
        }
      })
    );
    const topicSegmentReport = new TopicSegmentReport();
    topicSegmentReport.allTopicCharts = dataCharts;
    topicSegmentReport.removeTopics = removedSegments.map(segment => segment.name);
    const allActiveTopicIds = this.referenceService.activeTopics.map(tp => tp.id);
    topicSegmentReport.allTopics = allSegments.filter(seg => allActiveTopicIds.indexOf(seg.id) >= 0).map(seg => ({
      id: seg.id,
      name: seg.name
    }));
    return topicSegmentReport;
  }

  /**
   * API to get Best Performing Influencers report data from ES
   * @param companyId
   * @returns {Observable<Report>}
   */
  public getBestPerformingInfluencersFromES(measureCriteria: MeasureSearchCriteria)  {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetBestPerformingInfluencers, data).map(res => ({
      data: res.data,
      total: res.totalRow
    }));
  }

  /**
   * API to delete an existing report
   * @param companyId
   * @returns {Observable<Report>}
   */
  public deleteReport(reportId: number, companyId: number) {
    return this.delete(apiEndpoints.report + '/' + reportId, {companyId: companyId});
  }

  /**
   * API to get segment industries report data
   * @param companyId
   * @returns {Observable<Report>}
   */
  public loadSegmentIndutries(measureCriteria: MeasureSearchCriteria): Observable<SegmentIndustry> {
    const data = this.buildMeasureRequestData(measureCriteria);
    return this.get(apiEndpoints.measureGetSegmentIndustries, data).map(res => res.data);
  }

  /**
   * API to load ROI setting
   * @param companyId
   * @returns {Observable<Report>}
   */
  public loadRoiSetting(companyId): Observable<RoiSetting> {
    return this.get(apiEndpoints.getCPM, {company_id: companyId}).map(res => res && res.data ? Object.assign({}, res.data, {
      companyId: res.data.companyID,
      calculationMethod: res.data.method
    }) : null);
  }

  /**
   * API to update ROI setting
   * @param companyId
   * @returns {Observable<Report>}
   */
  public saveRoiSetting(roiSetting: RoiSetting): Observable<SegmentIndustry> {
    const submittedRoiSetting = Object.assign({}, roiSetting, {
      company_id: roiSetting.companyId,
      method: roiSetting.calculationMethod
    });
    return this.get(apiEndpoints.setCPM, submittedRoiSetting);
  }

  public loadOONAReportData(measureCriteria: MeasureSearchCriteria, limit: number): Observable<OnnaReportResponse> {
    const data = this.buildMeasureRequestData(measureCriteria, true);
    const params = Object.assign({}, data, {
      pageSize: limit
    });
    return this.get(apiEndpoints.measureGetOonaReport, params).map(res => res);
  }

  public exportCSV(projectId, influencerGroupId, fromDate, toDate) {
    return this.get(apiEndpoints.exportCSV, {
      projectId, influencerGroupId, fromDate, toDate
    });
  }

  private buildMeasureRequestData(measureCriteria: MeasureSearchCriteria, havePageNo?: boolean) {
    const dateRange = measureCriteria.period;
    if (havePageNo) {
      return {
        projectId: measureCriteria.project_id || measureCriteria.campaign_id || 0,
        influencerGroupId: measureCriteria.influencerGroup,
        fromDate: dateRange.startDate,
        toDate: dateRange.endDate,
        pageNo: measureCriteria.page_no
      };
    } else {
      return {
        projectId: measureCriteria.project_id || measureCriteria.campaign_id || 0,
        influencerGroupId: measureCriteria.influencerGroup,
        fromDate: dateRange.startDate,
        toDate: dateRange.endDate,
        orderBy: measureCriteria.orderBy
      };
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/measure/measure.service.ts